// === AANSPRAKELIJKHEID (AVP) — form validation ===
// Pure validation extracted from AansprakelijkheidScreen. validateAvp returns a map of i18n
// keys (the screen translates them); scrollToFirstError focuses the first errored field. Mirrors
// rechtsbijstand/validation.ts + personenauto/validation.ts.

type ValidationErrors = Record<string, string>;

export const AVP_ERROR_FIELD_IDS: Record<string, string> = {
    '_addressValidation': 'address-postcode',
    'Contractdocument.PP.BA.BA_PCODE': 'address-postcode',
    'Contractdocument.PP.BA.BA_HUISNR': 'address-huisnr',
    'Contractdocument.PP.BA.BA_TOEVOEG': 'address-toevoeging',
    'Contractdocument.PP.BA.BA_STRAAT': 'address-postcode',
    'Contractdocument.PP.BA.BA_PLAATS': 'address-postcode',
    'Contractdocument.PP.DA.DA_GSS': 'gezin',
};

export function areAvpRequiredFieldsFilled(dataToCheck: any): boolean {
    const ba = dataToCheck?.Contractdocument?.PP?.BA;

    // Address must be validated (street and city populated from lookup)
    if (!ba?.BA_STRAAT || !ba?.BA_PLAATS) {
      return false;
    }

    const requiredFields = {
      'DA_GSS': dataToCheck?.Contractdocument?.PP?.DA?.DA_GSS,
    };

    const allFilled = Object.entries(requiredFields).every(([_key, value]) => {
      const isFilled = value !== undefined && value !== null && value !== '';
      return isFilled;
    });

    return allFilled;
}

export function validateAvp(data: any): ValidationErrors {
    const errors: Record<string, string> = {};
    const ba = data?.Contractdocument?.PP?.BA;
    const da = data?.Contractdocument?.PP?.DA;

    // Postal code validation
    const postalCode = ba?.BA_PCODE || '';
    if (!postalCode) {
      errors['Contractdocument.PP.BA.BA_PCODE'] = 'screens.woonhuis.validation.postalCodeRequired';
    } else if (!/^\d{4}\s?[A-Za-z]{2}$/.test(postalCode)) {
      errors['Contractdocument.PP.BA.BA_PCODE'] = 'screens.aansprakelijkheid.validation.postalCodeInvalid';
    }

    // House number validation
    const houseNumber = ba?.BA_HUISNR || '';
    if (!houseNumber) {
      errors['Contractdocument.PP.BA.BA_HUISNR'] = 'screens.woonhuis.validation.houseNumberRequired';
    } else if (!/^[0-9]+$/.test(houseNumber)) {
      errors['Contractdocument.PP.BA.BA_HUISNR'] = 'screens.aansprakelijkheid.validation.houseNumberInvalid';
    }

    // Address validation - require lookup to be completed (street and city must be populated)
    if (!ba?.BA_STRAAT || !ba?.BA_PLAATS) {
      errors['_addressValidation'] = 'screens.aansprakelijkheid.validation.addressNotValidated';
    }

    // Required field validation
    if (!da?.DA_GSS) {
      errors['Contractdocument.PP.DA.DA_GSS'] = 'screens.aansprakelijkheid.validation.familyCompositionRequired';
    }

    // Check if calculation has been done
    const hasCalculatedPremium = !!(data?.Contractdocument?.PP?.DA?.DA_TTOT);
    if (!hasCalculatedPremium) {
      errors['_calculation'] = 'screens.aansprakelijkheid.validation.calculateFirst';
    }

    return errors;
}

export function scrollToFirstError(errors: ValidationErrors): void {
    const errorKeys = Object.keys(errors);
    if (errorKeys.length === 0) return;

    // Find the first error field that has a corresponding element ID
    for (const errorKey of errorKeys) {
      const elementId = AVP_ERROR_FIELD_IDS[errorKey];
      if (elementId) {
        const element = document.getElementById(elementId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          element.focus();
          return;
        }
      }
    }

    // Fallback to scrolling to top if no element found
    window.scrollTo({ top: 0, behavior: 'smooth' });
}
